import { FilterConditions, VariableValue } from '../../../core/models';
import { StoreLite } from '@mailok/store-lite';
import { map, shareReplay } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { buildCondition, buildDescription, FilterType, getFilterTypeByNumber } from '../../utils';

export interface ConditionsState {
    variable: VariableValue | null;
    filterType: FilterType;
    min: string;
    max: string;
    exact: string;
    name: string;
    condition: FilterConditions | null;
}

const initialState = (): ConditionsState => ({
    variable: null,
    filterType: FilterType.RANGE,
    min: '',
    max: '',
    exact: '',
    name: '',
    condition: null
});

export const filterFilterType = (filterType: FilterType) => (src: Observable<any>) =>
    src.pipe(map((ft: FilterType) => ft === filterType));

export default class ConditionsStore extends StoreLite<ConditionsState> {
    constructor() {
        super(initialState());
    }

    variable$ = this.select<VariableValue | null>(store => store.variable);
    filterType$ = this.select<FilterType>(store => store.filterType).pipe(shareReplay(1));
    min$ = this.select<string>(store => store.min);
    max$ = this.select<string>(store => store.max);
    exact$ = this.select<string>(store => store.exact);
    name$ = this.select<string>(store => store.name);
    unit$ = this.select<string>(store => (store.variable && store.variable.unit ? store.variable.unit : ''));
    greaterThan$ = this.filterType$.pipe(filterFilterType(FilterType.GREATER_THAN));
    lessThan$ = this.filterType$.pipe(filterFilterType(FilterType.LESS_THAN));
    equalTo$ = this.filterType$.pipe(filterFilterType(FilterType.EQUAL_TO));
    range$ = this.filterType$.pipe(filterFilterType(FilterType.RANGE));

    updateVariable(variable: VariableValue): void {
        const state = this.getStateSnapshot();
        const name = buildDescription(variable, state.filterType, state.min, state.max, state.exact);
        this.update({
            variable,
            name,
            condition: buildCondition(variable, state.filterType, state.min, state.max, state.exact, name)
        });
    }

    updateFilterType(filterType: FilterType): void {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, filterType, '', '', '');
        this.update({
            filterType,
            exact: '',
            min: '',
            max: '',
            name,
            condition: buildCondition(state.variable, filterType, '', '', '', name)
        });
    }

    clearMinMaxExact() {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, state.filterType, '', '', '');
        this.update({
            min: '',
            max: '',
            exact: '',
            name,
            condition: buildCondition(state.variable, state.filterType, '', '', '', name)
        });
    }

    updateMin(min: string) {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, state.filterType, min, state.max, state.exact);
        this.update({
            min,
            name,
            condition: buildCondition(state.variable, state.filterType, min, state.max, state.exact, name)
        });
    }

    updateMax(max: string) {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, state.filterType, state.min, max, state.exact);
        this.update({
            max,
            name,
            condition: buildCondition(state.variable, state.filterType, state.min, max, state.exact, name)
        });
    }

    updateExact(exact: string) {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, state.filterType, state.min, state.max, exact);
        this.update({
            exact,
            name,
            condition: buildCondition(state.variable, state.filterType, state.min, state.max, exact, name)
        });
    }

    reset() {
        const state = this.getStateSnapshot();
        const name = buildDescription(state.variable, state.filterType, '', '', '');
        this.update({
            min: '',
            max: '',
            exact: '',
            name,
            condition: buildCondition(state.variable, state.filterType, '', '', '', name)
        });
    }

    updateDescription(
        variable: VariableValue | null,
        filterType: FilterType,
        min: string,
        max: string,
        exact: string
    ): void {
        this.update({ name: buildDescription(variable, filterType, min, max, exact) });
    }

    clearState(): void {
        this.update(initialState());
    }

    getStateSnapshot(): ConditionsState {
        return this.selectSnapshot<ConditionsState>(store => store);
    }

    updateCondition(condition: FilterConditions | null): void {
        if (condition !== null) {
            const filterType = getFilterTypeByNumber(condition.typeFilter);
            if (filterType === FilterType.RANGE) {
                this.update({
                    variable: condition.variable,
                    filterType: filterType,
                    name: condition.name,
                    condition: condition,
                    min: `${condition.values[0]}`,
                    max: `${condition.values[1]}`,
                    exact: ''
                });
            } else if (filterType === FilterType.EQUAL_TO) {
                this.update({
                    variable: condition.variable,
                    filterType: filterType,
                    name: condition.name,
                    condition: condition,
                    min: '',
                    max: '',
                    exact: `${condition.values[0]}`
                });
            } else if (filterType === FilterType.GREATER_THAN) {
                this.update({
                    variable: condition.variable,
                    filterType: filterType,
                    name: condition.name,
                    condition: condition,
                    min: `${condition.values[0]}`,
                    max: '',
                    exact: ''
                });
            } else if (filterType === FilterType.LESS_THAN) {
                this.update({
                    variable: condition.variable,
                    filterType: filterType,
                    name: condition.name,
                    condition: condition,
                    min: '',
                    max: `${condition.values[0]}`,
                    exact: ''
                });
            }
        } else {
            this.update(initialState());
        }
    }
}
